export const SESSION_IO_VIEW_MODES = {
  STANDARD: 'standard',
  ADVANCED: 'advanced',
};

/**
 * Build the respondent-facing URL for a v2 dynamic-form component session
 * (dynamic_form, dynamic_review_task, form_step). Returns null when the
 * component type isn't a v2 form, appUrl can't be resolved, or no
 * formSessionId is recoverable from the session at all.
 *
 * Any v2 form-like can be a flow entry/member — when flowEntryId is set
 * the respondent always enters through the FormFlowPage shell so the
 * stepper, cross-step back nav, and save-and-exit work. Only standalone
 * v2 forms (no flowEntryId) use the single-form /dynamic-form route.
 *
 * flowEntryId present → /form-flow[-test]/{formSessionId} (admin host)
 * standalone          → /dynamic-form/{componentId}/{formSessionId}
 *
 * The canonical source for {formSessionId, flowEntryId} is
 * ComponentSession.data — every v2 form surface stamps them there at
 * WAITING/SUCCESS time:
 *   - dynamic_form trigger: DynamicFormController.submitForm seeds them
 *     into the webhook inputData; DynamicFormService.executeComponent
 *     copies them into the returned result (ComponentSession.data).
 *   - dynamic_review_task: DynamicReviewTaskService.executeWaitingStage
 *     stamps formSessionId (resolved to FlowSession UUID for flow
 *     members, frm_wf_* instance id for standalone).
 *   - form_step: FormStepService.executeWaitingStage stamps both,
 *     sourcing flowEntryId from the authoritative FlowSession.
 *
 * Legacy-session fallback: sessions persisted before the canonical
 * stamping landed may be missing one or both keys. For those, borrow
 * formSessionId from a sibling ComponentSession in the same
 * ProjectSession that is (a) the flow entry matching this component's
 * flowEntryId, or (b) declares the same flowEntryId and already has a
 * formSessionId stamped. Caller supplies `sessionComponents` (normally
 * `sessionData.components`); we silently skip the fallback when the list
 * is missing so unit-test fixtures without a full session still work.
 */
export const getV2FormSessionUrl = ({
  component,
  isTest,
  appUrl,
  sessionComponents,
}) => {
  if (!component) return null;
  const V2_TYPES = ['dynamic_form', 'dynamic_review_task', 'form_step'];
  if (!V2_TYPES.includes(component.type)) return null;

  const readFsid = c =>
    c?.data?.formSessionId || c?.data?.data?.formSessionId || '';
  const readFlowEntryId = c =>
    c?.data?.flowEntryId || c?.data?.data?.flowEntryId || '';

  let formSessionId = readFsid(component);
  const flowEntryId = readFlowEntryId(component);

  if (!formSessionId && flowEntryId && Array.isArray(sessionComponents)) {
    for (const other of sessionComponents) {
      if (!other || other === component) continue;
      const otherFsid = readFsid(other);
      if (!otherFsid) continue;
      const otherIsEntry = other.componentId === flowEntryId;
      const otherSharesFlow = readFlowEntryId(other) === flowEntryId;
      if (otherIsEntry || otherSharesFlow) {
        formSessionId = otherFsid;
        break;
      }
    }
  }

  if (!formSessionId) return null;

  const base = (appUrl || '').replace(/\/+$/, '');
  if (!base) return null;

  if (flowEntryId) {
    const path = isTest ? 'form-flow-test' : 'form-flow';
    return `${base}/${path}/${formSessionId}`;
  }
  return `${base}/dynamic-form/${component.componentId}/${formSessionId}`;
};

const asObject = value => {
  if (value == null) return {};
  return value;
};

const isPlainObject = value =>
  value != null && typeof value === 'object' && !Array.isArray(value);

export const hasIoEnvelope = component => Boolean(component?.io?.schemaVersion);

export const getDisplayStatus = component =>
  component?.io?.meta?.status || component?.status || '';

export const getDisplayError = component => {
  const ioError = component?.io?.diagnostics?.error;
  if (ioError == null || ioError === '') return component?.error || '';
  if (typeof ioError === 'string') return ioError;
  return JSON.stringify(ioError, null, 2);
};

export const getLegacyInputData = component => {
  if (!component) return {};

  const nested = component?.data?.data;
  const top = component?.data;
  const status = component?.status;

  if (status === 'SUCCESS' || status === 'SUCCESS_END') {
    const input = nested?.['.inputData'] ?? top?.['.inputData'];
    if (input == null) return {};
    return typeof input === 'string' ? input : { Input: input };
  }

  return asObject(nested || top || {});
};

export const getLegacyOutputData = component => {
  if (!component) return {};

  const payload = component?.data?.data ?? component?.data ?? {};
  if (!isPlainObject(payload)) return payload;

  const clone = { ...payload };
  delete clone['.inputData'];
  return clone;
};

export const getStandardInputData = component => {
  if (hasIoEnvelope(component)) {
    const ioInput = component?.io?.input?.resolved;
    if (ioInput != null) return ioInput;
  }
  return getLegacyInputData(component);
};

export const getStandardOutputData = component => {
  if (hasIoEnvelope(component)) {
    const ioOutput = component?.io?.output?.resolved;
    if (ioOutput != null) return ioOutput;
  }
  return getLegacyOutputData(component);
};

export const getInputBindingsData = component =>
  asObject(component?.io?.input?.bindings || []);

export const getExportMapData = component =>
  asObject(component?.io?.output?.exports || []);

export const getDiagnosticsData = component =>
  asObject(component?.io?.diagnostics || {});

export const getRawIoData = component =>
  asObject(component?.io || {});

export const shouldShowStandardExports = component => {
  if (!hasIoEnvelope(component)) return false;

  const exports = Array.isArray(component?.io?.output?.exports)
    ? component.io.output.exports.filter(x => x?.key)
    : [];
  if (!exports.length) return false;

  const output = component?.io?.output?.resolved;
  if (!isPlainObject(output)) return true;

  const outputKeys = new Set(Object.keys(output));
  if (!outputKeys.size) return true;

  return exports.some(exp => !outputKeys.has(exp.key));
};

export const getStandardExportSummaryData = component => {
  const exports = Array.isArray(component?.io?.output?.exports)
    ? component.io.output.exports.filter(x => x?.key)
    : [];

  const summary = {};
  for (const exp of exports) {
    summary[exp.key] = {
      type: exp.type || 'text',
      path: exp.path || exp.key,
      redacted: Boolean(exp.isRedacted),
    };
  }

  return summary;
};
